import { useCallback, useMemo, useRef, useState } from 'react';
import { Eye, Download, FileDown, Receipt } from 'lucide-react';
import { EmptyState } from '@/components/shared/empty-state';
import { PageHeader } from '@/components/shared/page-header';
import { Button } from '@/components/ui/button';
import { SupernovaDatePicker, type DateRange } from '@/components/ui/date-picker';
import {
  useCreditStatement,
  type CreditAccountSummary,
  type CreditAccountStatementRow,
} from '@/hooks/customer-engagement/use-credit-accounts';
import { useLocations } from '@/hooks/use-locations';
import { useLocationStore } from '@/stores/location-store';
import { usePosTenantConfig } from '@/hooks/use-pos-config';
import { useBusinessBranding } from '@/hooks/use-business-branding';
import { useAuthStore } from '@/stores/auth-store';
import { usePermissions } from '@/hooks/use-permissions';
import { formatCurrency, formatDateTime } from '@/lib/utils';
import { apiErrorMessage } from '@/lib/api-error';

/** Strip characters illegal in file names so the PDF download name is safe. */
function sanitizeFileName(name: string): string {
  return name
    .replace(/[\\/:*?"<>|]+/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

interface Props {
  customer: CreditAccountSummary;
  onBack: () => void;
}

/**
 * Full-page credit-account statement. Document-style layout: business + bill-to
 * header, account summary, and the transaction ledger. Supports a date-range
 * filter and a consolidated "Download PDF" of the whole (filtered) statement in
 * the standard From / Bill-To format — rendered off-screen and rasterized so
 * the per-row action buttons never appear in the PDF. The per-order View /
 * Download-PDF actions on each charge row are kept as-is.
 */
export function CreditStatementPage({ customer, onBack }: Props) {
  // Date-range filter (YYYY-MM-DD). Empty = full history. The statement hook
  // already forwards from/to to the API, which filters the ledger server-side.
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const { data, isLoading, isError, error } = useCreditStatement(
    customer.customerId,
    fromDate || undefined,
    toDate || undefined,
  );
  const { data: locationsResp } = useLocations();
  const { selectedLocationId, selectedLocationName } = useLocationStore();
  const tenantId = useAuthStore((s) => s.user?.tenantId) ?? '';
  // Downloads are POS reports' Export; viewing an order isn't.
  const canExport = usePermissions().has('reports.pos.export');
  const { data: brandingData } = useBusinessBranding(tenantId);

  // Resolve the real business name + contact the same way the per-order receipt
  // does — from the location's POS store config (pos_tenant_config.store_name),
  // not the branding title (often blank) or the location name. Falls back to a
  // charge's location when no location is actively selected.
  const firstChargeLocationId = useMemo(() => {
    for (const r of data?.data ?? []) {
      const l = (r.details as Record<string, unknown>)?.locationId;
      if (l) return String(l);
    }
    return '';
  }, [data]);
  const configLocationId = selectedLocationId || firstChargeLocationId;
  const { data: posConfigData } = usePosTenantConfig(configLocationId || undefined);

  // Branch (location) name + address for the FROM block, from the location record.
  const businessLocation = useMemo(() => {
    const loc = (locationsResp?.data ?? []).find((l) => l?.id === configLocationId);
    return {
      name: loc?.nickname || loc?.name || selectedLocationName || '',
      address: loc?.address || '',
    };
  }, [locationsResp, configLocationId, selectedLocationName]);

  // Business name (bold, top of FROM) — same resolution as the per-order receipt
  // / View PDF: store_name, then the branch name, then the branding title.
  const businessName =
    posConfigData?.data?.storeName?.trim() ||
    businessLocation.name ||
    brandingData?.data?.businessTitle?.trim() ||
    'Business';

  // Consolidated statement PDF: rasterize the off-screen printable node (which
  // omits the Actions column) into a single-page PDF sized to its content —
  // same html2canvas + jsPDF approach as the per-order receipt.
  const printRef = useRef<HTMLDivElement>(null);
  const [generating, setGenerating] = useState(false);
  const downloadStatementPdf = useCallback(async () => {
    const el = printRef.current;
    if (!el) return;
    setGenerating(true);
    try {
      const [{ default: html2canvas }, { jsPDF }] = await Promise.all([import('html2canvas-pro'), import('jspdf')]);
      const scale = 2;
      const canvas = await html2canvas(el, { scale, backgroundColor: '#ffffff' });
      const imgData = canvas.toDataURL('image/png');
      const w = canvas.width / scale;
      const h = canvas.height / scale;
      const pdf = new jsPDF({ unit: 'px', format: [w, h], orientation: w > h ? 'landscape' : 'portrait' });
      pdf.addImage(imgData, 'PNG', 0, 0, w, h);
      pdf.save(`${sanitizeFileName(`Credit Account Statement - ${customer.customerName}`)}.pdf`);
    } finally {
      setGenerating(false);
    }
  }, [customer.customerName]);

  // Resolve a location id to its display name so Details reads "Main Store"
  // rather than a raw UUID.
  const locationNameById = useMemo(() => {
    const map: Record<string, string> = {};
    for (const l of locationsResp?.data ?? []) {
      if (l?.id) map[l.id] = l.nickname || l.name || l.id;
    }
    return map;
  }, [locationsResp]);

  const rows = data?.data ?? [];

  // When a date range is active the summary must reflect the FILTERED ledger,
  // not the live account snapshot (which is always "as of today"). The
  // statement API returns only rows, so total the visible rows client-side:
  // charges add to what's owed, payments reduce it. On Clear (no range) we fall
  // back to the account's live balance / limit / past-due / available credit.
  const filterActive = Boolean(fromDate || toDate);
  const period = useMemo(() => {
    let charges = 0;
    let payments = 0;
    for (const r of rows) {
      const amt = Number(r.amount) || 0;
      if (r.type === 'payment') payments += amt;
      else charges += amt;
    }
    return { charges, payments, net: charges - payments };
  }, [rows]);

  const creditLimit = Number(customer.creditLimit);
  const balance = Number(customer.currentBalance);
  const pastDue = Number(customer.pastDueAmount);
  const availableCredit = creditLimit - balance;

  // A charge tied to a POS transaction can drill into that order's receipt.
  const orderIdOf = (details: Record<string, unknown>): string | null =>
    details.transactionId ? String(details.transactionId) : null;

  const openReceipt = (orderId: string, download: boolean) => {
    const suffix = download ? '?download=1' : '';
    window.open(`#/reports/orders/${orderId}/print${suffix}`, `order-print-${orderId}`);
  };

  return (
    <div className="space-y-4">
      <PageHeader title={`Statement — ${customer.customerName}`} onBack={onBack} />

      {/* Date-range filter — the same preset-first range picker as the other
          report pages. This view keeps its own UTC `YYYY-MM-DD` state (an empty
          range means "all history"), so it bridges to the picker here rather
          than reusing the reports' reporting-zone wrapper. */}
      <div className="flex flex-wrap items-center gap-3 rounded-xl border border-border bg-white px-4 py-3">
        <span className="text-sm font-medium text-foreground">Date Range</span>
        <SupernovaDatePicker
          mode="range"
          presetFirst
          portal
          compactDisplay
          autoApply={false}
          timezone="UTC"
          clearable
          className="w-[300px] max-w-full"
          value={{
            start: fromDate ? new Date(`${fromDate}T00:00:00Z`) : null,
            end: toDate ? new Date(`${toDate}T00:00:00Z`) : null,
          }}
          onApply={(v) => {
            const r = (v as DateRange | null) ?? { start: null, end: null };
            setFromDate(r.start ? r.start.toISOString().slice(0, 10) : '');
            setToDate(r.end ? r.end.toISOString().slice(0, 10) : '');
          }}
        />
        {(fromDate || toDate) && (
          <Button
            size="sm"
            variant="destructive-outline"
            onClick={() => {
              setFromDate('');
              setToDate('');
            }}
          >
            Clear
          </Button>
        )}
        {canExport && (
          <div className="ml-auto">
            <Button
              variant="primary"
              size="sm"
              disabled={isLoading || generating || rows.length === 0}
              onClick={downloadStatementPdf}
            >
              <FileDown className="h-3.5 w-3.5" />
              {generating ? 'Generating…' : 'Download PDF'}
            </Button>
          </div>
        )}
      </div>

      <div className="rounded-xl border border-border bg-white p-5 lg:p-6">
        {/* Business + statement meta */}
        <div className="flex flex-wrap items-start justify-between gap-4 border-b border-border pb-4">
          <div>
            <div className="text-lg font-semibold text-foreground">{businessName}</div>
          </div>
          <div className="text-right text-xs text-muted-foreground">
            <div className="tracking-wider">CREDIT ACCOUNT STATEMENT</div>
            <div className="mt-0.5">As of {formatDateTime(new Date().toISOString())}</div>
          </div>
        </div>

        {/* Bill to */}
        <div className="py-4">
          <div className="text-xs uppercase tracking-wide text-muted-foreground">Bill to</div>
          <div className="mt-1 font-medium text-foreground">{customer.customerName}</div>
          <div className="text-sm text-muted-foreground">{customer.customerPhone}</div>
        </div>

        {/* Account summary — period totals when a date range is active,
            otherwise the live account snapshot. */}
        <div className="mb-5 grid grid-cols-2 gap-3 md:grid-cols-4">
          {filterActive ? (
            <>
              <SummaryCard label="Charges (period)" value={formatCurrency(period.charges)} />
              <SummaryCard label="Payments (period)" value={formatCurrency(period.payments)} />
              <SummaryCard
                label="Net (period)"
                value={formatCurrency(period.net)}
                valueClass={period.net > 0 ? 'text-red-700' : 'text-foreground'}
              />
              <SummaryCard label="Credit limit" value={formatCurrency(creditLimit)} />
            </>
          ) : (
            <>
              <SummaryCard label="Balance due" value={formatCurrency(balance)} />
              <SummaryCard label="Credit limit" value={formatCurrency(creditLimit)} />
              <SummaryCard
                label="Past due"
                value={formatCurrency(pastDue)}
                valueClass={pastDue > 0 ? 'text-red-700' : 'text-foreground'}
              />
              <SummaryCard label="Available credit" value={formatCurrency(availableCredit)} />
            </>
          )}
        </div>

        {isLoading && <p className="py-6 text-center text-sm text-muted-foreground">Loading statement…</p>}
        {isError && (
          <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            Failed to load statement: {apiErrorMessage(error)}
          </div>
        )}
        {!isLoading && !isError && rows.length === 0 && (
          <EmptyState
            icon={Receipt}
            title="No activity for this customer"
            description="Charges and payments on this account will appear here."
            className="py-8"
          />
        )}

        {!isLoading && !isError && rows.length > 0 && (
          <div className="overflow-x-auto rounded-md border border-border">
            <table className="min-w-full divide-y divide-border">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium uppercase text-muted-foreground">When</th>
                  <th className="px-3 py-2 text-left text-xs font-medium uppercase text-muted-foreground">Type</th>
                  <th className="px-3 py-2 text-left text-xs font-medium uppercase text-muted-foreground">Reference</th>
                  <th className="px-3 py-2 text-left text-xs font-medium uppercase text-muted-foreground">Details</th>
                  <th className="px-3 py-2 text-right text-xs font-medium uppercase text-muted-foreground">Amount</th>
                  <th className="px-3 py-2 text-right text-xs font-medium uppercase text-muted-foreground">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {rows.map((r, i) => {
                  const orderId = orderIdOf(r.details);
                  return (
                    <tr key={i} className="hover:bg-gray-50">
                      <td className="whitespace-nowrap px-3 py-2 text-xs text-gray-700">
                        {formatDateTime(r.occurredAt)}
                      </td>
                      <td className="px-3 py-2 text-sm">
                        <span
                          className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
                            r.type === 'payment' ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'
                          }`}
                        >
                          {r.type}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-xs text-gray-700">{r.reference}</td>
                      <td className="px-3 py-2 text-xs text-gray-600">{formatDetails(r.details, locationNameById)}</td>
                      <td className="whitespace-nowrap px-3 py-2 text-right text-sm text-gray-900">
                        {r.type === 'payment' ? '-' : '+'}
                        {formatCurrency(Number(r.amount))}
                      </td>
                      <td className="px-3 py-2">
                        {orderId ? (
                          <div className="flex items-center justify-end gap-1 whitespace-nowrap">
                            <button
                              type="button"
                              onClick={() => openReceipt(orderId, false)}
                              className="inline-flex items-center gap-1 rounded px-2 py-1 text-xs text-gray-700 hover:bg-gray-100"
                              title="View order details"
                            >
                              <Eye className="h-3.5 w-3.5" /> View
                            </button>
                            {canExport && (
                              <button
                                type="button"
                                onClick={() => openReceipt(orderId, true)}
                                className="inline-flex items-center gap-1 rounded px-2 py-1 text-xs text-blue-700 hover:bg-blue-50"
                                title="Download order receipt as PDF"
                              >
                                <Download className="h-3.5 w-3.5" /> PDF
                              </button>
                            )}
                          </div>
                        ) : (
                          <div className="text-right text-xs text-gray-400">—</div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Off-screen printable statement — rasterized into the PDF. Excludes the
          per-row Actions column and app chrome. Always mounted so the ref is
          available for html2canvas. */}
      <div aria-hidden className="pointer-events-none fixed left-[-10000px] top-0">
        <div ref={printRef}>
          <PrintableStatement
            businessName={businessName}
            businessLocation={businessLocation}
            customer={customer}
            balance={balance}
            creditLimit={creditLimit}
            pastDue={pastDue}
            availableCredit={availableCredit}
            filterActive={filterActive}
            period={period}
            fromDate={fromDate}
            toDate={toDate}
            rows={rows}
            locationNameById={locationNameById}
          />
        </div>
      </div>
    </div>
  );
}

/** Standard-format statement document rendered off-screen and rasterized into
 *  the consolidated PDF. Inline styles (not Tailwind/CSS vars) so html2canvas
 *  resolves every colour reliably. No Actions column. */
function PrintableStatement({
  businessName,
  businessLocation,
  customer,
  balance,
  creditLimit,
  pastDue,
  availableCredit,
  filterActive,
  period,
  fromDate,
  toDate,
  rows,
  locationNameById,
}: {
  businessName: string;
  businessLocation: { name: string; address: string };
  customer: CreditAccountSummary;
  balance: number;
  creditLimit: number;
  pastDue: number;
  availableCredit: number;
  filterActive: boolean;
  period: { charges: number; payments: number; net: number };
  fromDate: string;
  toDate: string;
  rows: CreditAccountStatementRow[];
  locationNameById: Record<string, string>;
}) {
  const ink = '#111827';
  const muted = '#6b7280';
  const line = '#e5e7eb';
  const rangeLabel = fromDate || toDate ? `${fromDate || '…'} to ${toDate || '…'}` : 'All activity';
  const th: React.CSSProperties = {
    padding: '8px 10px',
    color: muted,
    fontSize: 11,
    textTransform: 'uppercase',
    borderBottom: `1px solid ${line}`,
  };
  const td: React.CSSProperties = { padding: '8px 10px', borderBottom: `1px solid ${line}`, verticalAlign: 'top' };

  return (
    <div
      style={{
        width: 820,
        background: '#fff',
        color: ink,
        padding: 40,
        fontFamily: 'Arial, Helvetica, sans-serif',
        boxSizing: 'border-box',
      }}
    >
      {/* Header */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          borderBottom: `2px solid ${ink}`,
          paddingBottom: 16,
        }}
      >
        <div style={{ fontSize: 24, fontWeight: 700 }}>{businessName}</div>
        <div style={{ textAlign: 'right', fontSize: 12, color: muted }}>
          <div style={{ letterSpacing: 2, fontWeight: 700, color: ink }}>CREDIT ACCOUNT STATEMENT</div>
          <div style={{ marginTop: 4 }}>As of {formatDateTime(new Date().toISOString())}</div>
          <div>Period: {rangeLabel}</div>
        </div>
      </div>

      {/* From / Bill To */}
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 24, marginTop: 20 }}>
        <div style={{ width: '48%' }}>
          <div style={{ fontSize: 11, letterSpacing: 1, color: muted, marginBottom: 6 }}>FROM</div>
          <div style={{ fontWeight: 700 }}>{businessName}</div>
          {businessLocation.name && businessLocation.name !== businessName && (
            <div style={{ fontSize: 13 }}>{businessLocation.name}</div>
          )}
          {businessLocation.address && <div style={{ fontSize: 13, color: muted }}>{businessLocation.address}</div>}
        </div>
        <div style={{ width: '48%' }}>
          <div style={{ fontSize: 11, letterSpacing: 1, color: muted, marginBottom: 6 }}>BILL TO</div>
          <div style={{ fontWeight: 700 }}>{customer.customerName}</div>
          <div style={{ fontSize: 13, color: muted }}>{customer.customerPhone}</div>
        </div>
      </div>

      {/* Summary — period totals when filtered, else live account snapshot. */}
      <div style={{ display: 'flex', gap: 12, marginTop: 20 }}>
        {(filterActive
          ? ([
              ['Charges (period)', formatCurrency(period.charges), ink],
              ['Payments (period)', formatCurrency(period.payments), ink],
              ['Net (period)', formatCurrency(period.net), period.net > 0 ? '#b91c1c' : ink],
              ['Credit limit', formatCurrency(creditLimit), ink],
            ] as const)
          : ([
              ['Balance due', formatCurrency(balance), ink],
              ['Credit limit', formatCurrency(creditLimit), ink],
              ['Past due', formatCurrency(pastDue), pastDue > 0 ? '#b91c1c' : ink],
              ['Available credit', formatCurrency(availableCredit), ink],
            ] as const)
        ).map(([label, value, color]) => (
          <div key={label} style={{ flex: 1, border: `1px solid ${line}`, borderRadius: 6, padding: 12 }}>
            <div style={{ fontSize: 11, color: muted }}>{label}</div>
            <div style={{ fontSize: 18, fontWeight: 700, color, marginTop: 4 }}>{value}</div>
          </div>
        ))}
      </div>

      {/* Ledger — no Actions column */}
      <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 20, fontSize: 12 }}>
        <thead>
          <tr style={{ background: '#f9fafb' }}>
            <th style={{ ...th, textAlign: 'left' }}>When</th>
            <th style={{ ...th, textAlign: 'left' }}>Type</th>
            <th style={{ ...th, textAlign: 'left' }}>Reference</th>
            <th style={{ ...th, textAlign: 'left' }}>Details</th>
            <th style={{ ...th, textAlign: 'right' }}>Amount</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>
              <td style={{ ...td, whiteSpace: 'nowrap' }}>{formatDateTime(r.occurredAt)}</td>
              <td style={{ ...td, textTransform: 'capitalize' }}>{r.type}</td>
              <td style={td}>{r.reference}</td>
              <td style={{ ...td, color: muted }}>{formatDetails(r.details, locationNameById)}</td>
              <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                {r.type === 'payment' ? '-' : '+'}
                {formatCurrency(Number(r.amount))}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div style={{ marginTop: 20, fontSize: 11, color: muted }}>
        Generated on {formatDateTime(new Date().toISOString())}
      </div>
    </div>
  );
}

function SummaryCard({ label, value, valueClass }: { label: string; value: string; valueClass?: string }) {
  return (
    <div className="rounded-md bg-gray-50 p-3">
      <div className="text-xs text-gray-500">{label}</div>
      <div className={`mt-0.5 text-xl font-semibold ${valueClass ?? 'text-foreground'}`}>{value}</div>
    </div>
  );
}

const AGING_LABELS: Record<string, string> = {
  '0-30': '0–30 days',
  '31-60': '31–60 days',
  '61-90': '61–90 days',
  '90+': '90+ days',
};

const PAYMENT_METHOD_LABELS: Record<string, string> = {
  cash: 'Cash',
  card: 'Card',
  check: 'Check',
  ach: 'ACH',
};

/** Render the per-row details into a readable, UUID-free string.
 *  Payments → method + note; charges → location name + aging bucket. */
function formatDetails(details: Record<string, unknown>, locationNameById: Record<string, string>): string {
  const parts: string[] = [];
  if (details.method) {
    const m = String(details.method);
    parts.push(`Method: ${PAYMENT_METHOD_LABELS[m] ?? m}`);
  }
  if (details.notes) parts.push(`Note: ${String(details.notes)}`);
  const locId = details.locationId ? String(details.locationId) : '';
  if (locId) parts.push(locationNameById[locId] ?? 'Unknown location');
  if (details.agingBucket) {
    const b = String(details.agingBucket);
    parts.push(AGING_LABELS[b] ?? b);
  }
  return parts.join(' · ');
}
